import { useState, useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { captureLead } from '../../services/leadCapture';
import { LEAD_SOURCES } from '../../services/contactApi';
import { trackB2bCtaClick } from '../../utils/analytics/b2bTracking';

interface B2bCtaForm {
  email: string;
}

const DISMISS_STORAGE_KEY = 'rt:b2b-cta-dismissed';
const CTA_SOURCE = 'b2b_cta_banner';

const POPIA_NOTICE =
  'POPIA notice: your email is used for recruiter/HR follow-up only. We will not share it with third parties.';

function readDismissed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DISMISS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DISMISS_STORAGE_KEY, '1');
  } catch {
    // localStorage may be unavailable (private mode / quota) — fail silently.
  }
}

export default function B2bCta() {
  const [dismissed, setDismissed] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<B2bCtaForm>();

  useEffect(() => {
    setDismissed(readDismissed());
  }, []);

  const handleCtaClick = () => {
    trackB2bCtaClick(CTA_SOURCE);
    setShowForm(true);
  };

  const handleDismiss = () => {
    setDismissed(true);
    writeDismissed();
  };

  const onSubmit: SubmitHandler<B2bCtaForm> = async ({ email }) => {
    setIsSubmitting(true);
    setStatus(null);

    try {
      const result = await captureLead({ email, source: LEAD_SOURCES.B2B });
      setStatus({
        type: 'success',
        message: result.message || "Thanks! We'll be in touch about the recruiter/HR tool.",
      });
      reset();
    } catch {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (dismissed) return null;

  return (
    <section
      aria-labelledby="b2b-cta-title"
      className="mt-6 border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-5"
      data-cta-source={CTA_SOURCE}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 id="b2b-cta-title" className="text-base font-semibold text-charcoal dark:text-slate-100">
            Grossing up salaries for your team? Talk to us about a recruiter/HR tool.
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-slate-300">
            We&rsquo;re building a recruiter/HR tool for bulk gross-up and payroll benchmarking.
            Drop your email and we&rsquo;ll reach out.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss recruiter/HR CTA"
          className="flex-shrink-0 -m-1 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-black/5 dark:hover:bg-white/10 rounded text-2xl leading-none"
        >
          ×
        </button>
      </div>

      <div className="mt-3">
        {!showForm ? (
          <button
            type="button"
            onClick={handleCtaClick}
            className="btn-primary"
          >
            Talk to us
          </button>
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col gap-2"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1">
                <label htmlFor="b2b-email" className="sr-only">
                  Email address
                </label>
                <input
                  id="b2b-email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Please enter a valid email address',
                    },
                  })}
                  className="h-12 w-full px-4 rounded-lg text-charcoal focus:ring-2 focus:ring-sa-green focus:ring-offset-0 focus:outline-none disabled:opacity-60"
                  placeholder="you@example.com"
                  disabled={isSubmitting}
                  aria-invalid={!!errors.email}
                  aria-describedby="b2b-popia-notice"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary inline-flex h-12 items-center justify-center px-6 whitespace-nowrap"
              >
                {isSubmitting ? 'Sending…' : "Let's talk"}
              </button>
            </div>

            {errors.email && (
              <p className="text-sm text-error" role="alert">
                {errors.email.message}
              </p>
            )}
            <p id="b2b-popia-notice" className="text-xs text-gray-600 dark:text-slate-400">
              {POPIA_NOTICE}
            </p>
          </form>
        )}
      </div>

      {status && (
        <p
          role="status"
          aria-live="polite"
          className={`mt-3 text-sm ${status.type === 'success' ? 'text-sa-green' : 'text-error'}`}
        >
          {status.message}
        </p>
      )}
    </section>
  );
}
